import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Pencil, Trash2, Check } from 'lucide-react'
import axios from '../../../../config/axios'
import { usePipelineContext } from '../PipelineContext'
import * as S from './style'
import styled from 'styled-components'

const SwitchToggle = styled.div<{ active?: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: ${props => props.active ? '#0090D3' : '#E5E5E5'};
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;
`

const SwitchThumb = styled.div<{ active?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  position: absolute;
  left: ${props => props.active ? '22px' : '2px'};
  transition: all 0.2s ease;
  color: #0090D3;
`

interface Automation {
  id: number
  name: string
  description: string
  isActive: boolean
  triggerTable: string
  triggerEvent: string
}

const Breadcrumbs: React.FC<{ items: { label: string; path?: string }[] }> = ({
  items,
}) => (
  <S.BreadcrumbsContainer>
    <ol>
      {items.map((item, index) => (
        <li key={index}>
          {index === items.length - 1 ? (
            <span>{item.label}</span>
          ) : item.path ? (
            <Link to={item.path}>{item.label}</Link>
          ) : (
            <span style={{color: '#0090d3', cursor: 'pointer'}}>{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </S.BreadcrumbsContainer>
)

const AutomationsPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const pipelineId = searchParams.get('pipelineId')
  const { activeAutomationTab, pipelineName } = usePipelineContext()
  const [automations, setAutomations] = useState<Automation[]>([
    {
      id: 1,
      name: 'Enviar e-mail de boas-vindas',
      description: 'Envia um e-mail automaticamente quando um novo lead entra no funil',
      isActive: true,
      triggerTable: 'leads',
      triggerEvent: 'Novo lead criado'
    },
    {
      id: 2,
      name: 'Mover para próxima etapa',
      description: 'Move o card automaticamente após 3 dias sem interação',
      isActive: false,
      triggerTable: 'cards',
      triggerEvent: 'Card atualizado'
    }
  ])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    fetchAutomations()
  }, [pipelineId])

  const fetchAutomations = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation`
      )
      if (response.data?.data) {
        setAutomations(response.data.data)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation/${id}`,
        { isActive: !currentStatus }
      )
      setAutomations(prev => 
        prev.map(a => a.id === id ? { ...a, isActive: !currentStatus } : a)
      )
    } catch (error) {
      console.error(error)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta automação?')) return
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/automation-service/automation/${id}`
      )
      setAutomations(prev => prev.filter(a => a.id !== id))
    } catch (error) {
      console.error(error)
    }
  }

  const breadcrumbItems = [
    { label: 'Funis', path: '/panel/pipelines' },
    { label: pipelineName || 'Nome do funil' },
    { label: 'Automação' },
  ]

  return (
    <S.Container>
      <Breadcrumbs items={breadcrumbItems} />
      <S.Content>
        {activeAutomationTab === 'automations' && (
          <S.Table>
            <S.TableHeader>
              <S.TableHeaderCell width="8%"></S.TableHeaderCell>
              <S.TableHeaderCell width="25%">Nome</S.TableHeaderCell>
              <S.TableHeaderCell width="22%">Evento</S.TableHeaderCell>
              <S.TableHeaderCell width="35%">Descrição</S.TableHeaderCell>
              <S.TableHeaderCell width="10%"></S.TableHeaderCell>
            </S.TableHeader>
            <S.TableBody>
              {automations.length === 0 && !isLoading ? (
                <S.EmptyState>
                  Nenhuma automação cadastrada
                </S.EmptyState>
              ) : (
                automations.map((automation) => (
                  <S.TableRow key={automation.id}>
                    <S.TableCell>
                      <SwitchToggle 
                        active={automation.isActive}
                        onClick={() => handleToggleActive(automation.id, automation.isActive)}
                      >
                        <SwitchThumb active={automation.isActive}>
                          {automation.isActive && <Check size={12} />}
                        </SwitchThumb>
                      </SwitchToggle>
                    </S.TableCell>
                    <S.TableCell>{automation.name}</S.TableCell>
                    <S.TableCell>{automation.triggerEvent || automation.triggerTable}</S.TableCell>
                    <S.TableCell>{automation.description || '-'}</S.TableCell>
                    <S.TableCell>
                      <S.ActionButtons>
                        <S.ActionButton>
                          <Pencil size={18} />
                        </S.ActionButton>
                        <S.ActionButton onClick={() => handleDelete(automation.id)}>
                          <Trash2 size={18} />
                        </S.ActionButton>
                      </S.ActionButtons>
                    </S.TableCell>
                  </S.TableRow>
                ))
              )}
            </S.TableBody>
          </S.Table>
        )}

        {activeAutomationTab === 'connections' && (
          <S.EmptyState>Conexões em desenvolvimento</S.EmptyState>
        )}

        {activeAutomationTab === 'integration' && (
          <S.EmptyState>Integração em desenvolvimento</S.EmptyState>
        )}

        {activeAutomationTab === 'documentation' && (
          <S.EmptyState>Documentação em desenvolvimento</S.EmptyState>
        )}
      </S.Content>
    </S.Container>
  )
}

export default AutomationsPage
